import { useEffect, useRef, useState, type ReactNode } from "react";

import {
  boardPage,
  EXPORTS,
  ORDER_CHOICES,
  PER_PAGE_CHOICES,
  type BoardOrder,
  type BoardRow,
  type ExportFormat,
} from "../board.ts";
import { stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { RowDetail } from "./RowDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/*
 * **Copied from `apps/tenant-admin-console/src/ui/RecordsBoard.tsx`, unchanged.** Its own reasoning is
 * about that console's nine pages; this one has one board. `../board.ts` carries the note on why the
 * copy is still right — in one sentence, this component *is* design rule 4 and the row detail panel,
 * and the fifteen pages this console cannot yet draw are the same template.
 *
 * The geometry it draws is the same geometry: `22 WP21 Merchant Portal.dc.html` carries `gridUI()`,
 * `badge()` and `hdrUI()` byte-identically with the other work-package files, which was measured
 * rather than assumed. So "built to the tenant admin design" and "built to the platform design" name
 * the same measurements below.
 */

/**
 * The board every page of `17 WP16 Tenant Admin.dc.html` is, drawn once.
 *
 * That file renders all fifteen of its pages from one `PAGES` row and one `gridUI()`: a crumb
 * trail, a title with a mono source chip and a primary command, a sentence, four KPI tiles, a card
 * holding a toolbar, a header strip, rows, an empty state and a pager, then a closing callout — and
 * a detail panel over the top of it when a row is clicked. Every value below is that file's, read
 * from its own inline styles and already measured into `styles.css` by the four screens built
 * before these five.
 *
 * **Why this component exists and those four do not use it.** Five more pages of the same template
 * were about to be five more ~500-line copies of search, sort, paging, three export formats, an
 * empty state and a pager. The four that exist are each pinned by a committed fidelity check, and
 * rewriting a measured screen to share code with an unmeasured one is how a green check stops
 * meaning anything — so they are left exactly as they are, and this is written for the five that
 * were not drawn yet. `docs/OPEN_WORK.md` carries the consolidation with that condition on it.
 *
 * What a page keeps for itself is everything that is a statement about a service: which operation
 * it reads, what it makes of a row, what state a row is in, which filter its listing can honestly
 * offer, and the words. This component decides nothing about any of that. It owns the three things
 * the design makes identical on every page — the grid's behaviour, the states, and the geometry.
 *
 * Design rule 4 is satisfied here rather than nine times: search, filter, sort, pagination with
 * rows-per-page, export to CSV, Excel and PDF, and an empty state with a Clear filters button.
 */

export interface RecordsBoardProps {
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs: readonly string[];
  readonly title: string;
  /** The service codes behind the page, in the mono chip the design puts beside the title. */
  readonly source: string;
  readonly description: string;
  readonly columns: readonly string[];
  /** `undefined` until the listing has answered, which is the design's loading state. */
  readonly rows: readonly BoardRow[] | undefined;
  /** Whatever went wrong, already mapped to a state by `view-state.ts`. */
  readonly failure?: ViewState;
  readonly onRetry: () => void;
  /** The empty state's sentence: what would have to happen for a row to appear. */
  readonly purpose: string;
  /**
   * The design's primary command, and why it is refused — **absent when the design draws none**.
   *
   * Most of these pages have a designed form whose required fields include at least one with no
   * field on the wire, so they are drawn and disabled with the measured reason on the button. A page
   * offering a command it cannot send is the stub `PageOutlook` exists to avoid.
   *
   * It is optional because **`platform · Event catalogue and dead letters` draws no primary command
   * at all**: the design file has no `FORMS.dlq` entry, so its own `formUI` takes the `!F` branch
   * and returns `btnS:{display:'none'}`. This prop was required, so that board drew a button its
   * design does not have — which `CLAUDE.md` §1 forbids in the same breath as removing one. Drawing
   * a disabled control the design omits is not a safe default; it is an added section.
   */
  readonly primary?: { readonly label: string; readonly refusal: string };
  /** The filter's options, in the service's own vocabulary, and the design's word for unfiltered. */
  readonly filter: {
    readonly unfiltered: string;
    readonly options: readonly { readonly value: string; readonly label: string }[];
    readonly value: string;
    readonly onChange: (value: string) => void;
  };
  /** The design's closing callout, in one of its three kinds. */
  readonly note: {
    readonly kind: "info" | "warn" | "lock";
    readonly glyph: string;
    readonly text: string;
  };
  /** What the board fetched, where that is worth saying. `boundedNote` decides. */
  readonly bounded?: string;
  /**
   * A sentence under the grid saying what the listing is a list of, where that is not obvious.
   *
   * The service catalogue's precedent: a board whose operation silently excludes records is a
   * complete-looking list with records missing from it, and the fix is a sentence rather than a
   * guess at the total.
   *
   * **It qualifies what is drawn. It does not narrate what is absent.** No design draws this block
   * at all, so it is an addition that `CLAUDE.md` section 1 tolerates for one reason: a figure on
   * screen that a reader would otherwise misread. `platform-flags` set the accepted scale at two
   * lines — "a fraction in the State column counts the tenants that hold that flag, not the tenants
   * on the platform" — and every word of that earns its place by changing how a drawn cell reads.
   *
   * A cell the board does not draw needs no caption, because nobody misreads a column of em dashes.
   * Four boards landed with the absences recited here instead: `slo`'s ran to eleven lines on a
   * 768px screen, naming a burn rate, an SLI URN, four in-process counters and an unmodelled alert
   * tile, none of which is on the page. The fidelity harness measured the cost and it was not
   * small — those four scored two to three times worse against their designs in dark theme than in
   * light, while the three boards with a short note scored the same in both. The reason is
   * arithmetic rather than taste: a long amber block is a large area of saturated colour where the
   * design has empty page, and the same misplacement costs far more pixels against a dark ground
   * than a pale one. It had also grown the page back to roughly the design's height, which moved
   * the difference out of the harness's "different sizes" column and into its "inside the overlap"
   * column — so the number that was supposed to be the honest one was the one being inflated.
   *
   * Where an absence needs recording, the places for it are the board module's own docblock,
   * `navigation.ts`'s page entry, and `docs/design/DESIGN_QUESTIONS.md`. All three already hold it.
   */
  readonly footnote?: string;
  /** The basename an export writes, and the heading of its first column set. */
  readonly exportName: string;
  /** The design's detail-panel subtitle: the page's title and its source chip. */
  readonly detailSubtitle: string;
  /** The sentence the design repeats inside the detail panel, which is the page's closing note. */
  readonly detailNote: string;
}

export function RecordsBoard(props: RecordsBoardProps): ReactNode {
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<BoardOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const [openRow, setOpenRow] = useState<string | undefined>(undefined);
  const exportRegion = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const view = boardPage(props.rows ?? [], { search, order, page, perPage });

  /** Whether anything the administrator chose is narrowing the board. */
  const narrowed = props.filter.value !== "" || search.trim().length > 0;

  /*
   * Which of the six states this board is in.
   *
   * **`stateForList` is consulted for the unfiltered cases and deliberately not for the filtered
   * one, and that is a defect in the four screens built before these five rather than a difference
   * of taste.** Handed an empty list with `filtered: true` it answers `empty/filtered`, and
   * `StateView` then draws "Nothing matches this filter · Clear the filter to see everything on
   * this page" **in place of its children** — so the grid's own `search_off` block, with the
   * **Clear filters button design rule 4 requires**, is never rendered. On those four screens the
   * button is reachable only when a client-side search empties a board that had rows; choose a
   * server-side filter that answers nothing and the sentence tells the administrator to clear a
   * filter and gives them no way to.
   *
   * The design has no such split: its `grid.empty` block sits inside the card, above the pager,
   * whenever the row count is zero for any reason. So a narrowed board stays `ready` here and the
   * card draws its own empty state, and `StateView`'s empty state is kept for the one case it is
   * right for — a tenant that genuinely has no rows, where there is no filter to clear and the
   * sentence worth showing is what would make one appear. Recorded in `docs/OPEN_WORK.md` against
   * the four earlier screens, because fixing them means re-measuring them.
   *
   * No `observedAt` on any of these four listings, so no stale state is reachable. Each publishes
   * per-row instants — `updatedAt`, `approvedAt` — and no page-level instant saying when the answer
   * was composed. A configuration board is not a live board, so this is the least costly place that
   * absence has been found; it is recorded all the same, because the recommendation is the same one
   * and it is one field. R-08.
   */
  const viewState: ViewState =
    props.failure ??
    (narrowed && props.rows !== undefined
      ? { kind: "ready", stale: false }
      : stateForList(props.rows));

  const clearFilters = (): void => {
    setSearch("");
    props.filter.onChange("");
    setPage(1);
  };

  const detail = view.all.find((row) => row.id === openRow);

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      {props.columns.map((column) => (
        <span key={column}>{column}</span>
      ))}
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <section className="page records">
      <PageHeading
        crumbs={props.crumbs}
        title={props.title}
        source={props.source}
        description={props.description}
        action={
          props.primary === undefined ? undefined : (
            <button
              type="button"
              className="records__primary"
              disabled
              title={props.primary.refusal}
            >
              <span aria-hidden="true" className="records__primary-glyph">
                add
              </span>
              {props.primary.label}
            </button>
          )
        }
      />

      {/*
        No tiles, where the design has four on every one of these pages.

        Twenty tiles across the five, and not one has an operation behind it. Nine are counts or
        values derived from a count — R-04's measurement is that none of these four listings
        publishes a total, so a tile reading `24` would mean "24 so far" under a label that means
        "24". Nine more are a page's own absent column restated: all four of `config`'s are Q-104,
        all four of `plan`'s are Q-105, and `policies`'s `Next effective change` is Q-101. The last
        two, `Surge zones now` and `Promotions`, belong to the dynamic-pricing and promotion
        services, neither of which is an owner of the pricing page. Each page's component names its
        own four.
      */}

      <div className="records__panel">
        <div className="records__toolbar">
          <span className="records__search">
            <span aria-hidden="true" className="records__search-glyph">
              search
            </span>
            <input
              type="search"
              aria-label="Search"
              placeholder="Search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </span>
          <select
            aria-label="Filter by state"
            value={props.filter.value}
            onChange={(event) => {
              props.filter.onChange(event.target.value);
              setPage(1);
            }}
          >
            <option value="">{props.filter.unfiltered}</option>
            {props.filter.options.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Sort"
            value={order}
            onChange={(event) => {
              setOrder(event.target.value as BoardOrder);
            }}
          >
            {ORDER_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <span className="records__export" ref={exportRegion}>
            <button
              type="button"
              className="records__export-button"
              aria-expanded={exportOpen}
              onClick={() => {
                setExportOpen((open) => !open);
              }}
            >
              <span aria-hidden="true" className="records__export-glyph">
                download
              </span>
              Export
            </button>
            {exportOpen ? (
              <div className="records__export-menu" role="menu">
                {EXPORTS.map((choice) => (
                  <button
                    key={choice.format}
                    type="button"
                    role="menuitem"
                    className="records__export-item"
                    onClick={() => {
                      setExportOpen(false);
                      exportRows(view.all, props.columns, choice.format, props.exportName);
                    }}
                  >
                    <span aria-hidden="true" className="records__export-item-glyph">
                      {choice.glyph}
                    </span>
                    {choice.label}
                  </button>
                ))}
              </div>
            ) : null}
          </span>
        </div>

        <StateView
          state={viewState}
          purpose={props.purpose}
          onRetry={() => {
            props.onRetry();
          }}
          skeleton={{
            head,
            // The columns this board was given, plus the `State` column it adds itself.
            columns: props.columns.length + 1,
            rowClassName: "records__row",
            page: props.title,
            // The first crumb is the tenant, which is how this board is always called.
            tenant: props.crumbs[0] ?? "this tenant",
          }}
        >
          {head}
          <div className="records__rows">
            {view.rows.map((row) => (
              <div
                key={row.id}
                className={
                  row.tinted === true ? "records__row records__row--cross" : "records__row"
                }
                /*
                 * `div role="button" tabindex="0"`, which is the design's own markup for a grid row
                 * — not a `<button>`. A real button brings the user agent's border, background,
                 * font and text alignment, and resetting those is five rules of guesswork where the
                 * design has already decided: its row is a grid container that happens to be
                 * operable.
                 *
                 * The four screens built before these five draw a plain `div` with no role, no tab
                 * stop and no click, which is the one place their markup is not this design's
                 * behaviour: that file puts `cursor: pointer`, `role`, `tabindex` and an `onClick`
                 * on every row of every page, and opens a detail panel from it.
                 *
                 * The keyboard handler is this console's rather than the design's. That file binds
                 * only `onClick`, so its rows are reachable by Tab and cannot be activated — which
                 * is a defect in the reference rather than a decision to copy.
                 */
                role="button"
                tabIndex={0}
                onClick={() => setOpenRow(row.id)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  setOpenRow(row.id);
                }}
              >
                {row.cells.map((cell, index) => (
                  <span
                    key={props.columns[index] ?? String(index)}
                    className={cellClass(index, cell.mono === true, cell.absent === true)}
                    {...(cell.title === undefined ? {} : { title: cell.title })}
                  >
                    {cell.absent === true ? "—" : cell.text}
                  </span>
                ))}
                <span className="records__state">
                  <StateBadge
                    code={props.source}
                    status={row.state.label}
                    classKey={row.state.classKey}
                    label={row.state.label}
                    {...(row.state.title === undefined ? {} : { title: row.state.title })}
                  />
                </span>
              </div>
            ))}
          </div>
          {view.total === 0 ? (
            <div className="records__no-match">
              <span aria-hidden="true" className="records__no-match-glyph">
                search_off
              </span>
              Nothing matches this search or filter.
              <button type="button" className="records__clear" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          ) : null}
          <div className="records__pager">
            <span>
              {view.total === 0
                ? "No matching rows"
                : `Showing ${String((Math.min(page, view.pageCount) - 1) * perPage + 1)}–${String(Math.min(Math.min(page, view.pageCount) * perPage, view.total))} of ${String(view.total)}`}
            </span>
            <span className="records__pager-controls">
              <select
                aria-label="Rows per page"
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
              >
                {PER_PAGE_CHOICES.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice} per page
                  </option>
                ))}
              </select>
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <span aria-hidden="true">chevron_left</span>
              </button>
              <span className="records__page">
                Page {Math.min(page, view.pageCount)} of {view.pageCount}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={page >= view.pageCount}
                onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
              >
                <span aria-hidden="true">chevron_right</span>
              </button>
            </span>
          </div>
        </StateView>

        {props.bounded === undefined ? null : (
          <p className="records__bounded" role="status">
            {props.bounded}
          </p>
        )}
        {props.footnote === undefined ? null : <p className="records__bounded">{props.footnote}</p>}
      </div>

      <div className={`records__note records__note--${props.note.kind}`}>
        <span aria-hidden="true" className="records__note-glyph">
          {props.note.glyph}
        </span>
        <span>{props.note.text}</span>
      </div>

      {detail === undefined ? null : (
        <RowDetail
          row={detail}
          columns={props.columns}
          subtitle={props.detailSubtitle}
          source={props.source}
          note={props.detailNote}
          /*
           * One prop decides both controls, because the design decides both from one thing. Its
           * `formUI` reads `FORMS[page]` once and returns `btnS` and `editS` together: a page
           * with no form entry draws neither the header's command nor the panel's `Edit`. Passing
           * `primary`'s absence through here keeps that single source rather than letting a board
           * hide one and keep the other, which would be a state the design file cannot express.
           */
          editable={props.primary !== undefined}
          onClose={() => setOpenRow(undefined)}
        />
      )}
    </section>
  );
}

/**
 * Which of the grid's three cell rules a column takes.
 *
 * The design's own rule: the first cell is the row's name at 600 in `--text`, the rest at 400 in
 * `--text2`. The class names are the ones already measured into `styles.css` by the audit board,
 * whose first column is a clock — hence `__when` for a cell that is usually a name.
 */
function cellClass(index: number, mono: boolean, absent: boolean): string {
  const base = index === 0 ? "records__when" : index === 1 ? "records__actor" : "records__action";
  return `${base}${mono ? " records__cell--mono" : ""}${absent ? " records__cell--absent" : ""}`;
}

/**
 * Export what the administrator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen rather than the raw
 * values — so an exported row says the same thing the board did, including an absent cell, which is
 * written as the design's own `—` rather than as an empty field. An empty field in a spreadsheet
 * reads as "nothing here"; `—` under a column that can never be filled is the same statement the
 * screen made.
 */
function exportRows(
  rows: readonly BoardRow[],
  columns: readonly string[],
  format: ExportFormat,
  name: string,
): void {
  const header = [...columns, "State"];
  const body = rows.map((row) => [
    ...row.cells.map((cell) => (cell.absent === true ? "—" : cell.text)),
    row.state.label,
  ]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), `${name}.csv`);
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      `${name}.xls`,
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>${escapeHtml(name)}</title></head><body><h2 style="font-family:sans-serif">${escapeHtml(name)}</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}
